import { supabase } from '../supabase';

// ─── Paid PDF books (Google Play one-time purchases) ─────────────────────────
//
// Android only. These are non-consumable Play products granting lifetime access,
// and are a completely separate mechanism from `ebookApi.ts`, where a rewarded
// ad unlocks a free book for 7 days. Nothing here touches that path.
//
// The trust boundary sits at the Edge Functions, not in this file. `paid_books`
// is a public catalogue, `book_purchases` is readable-but-not-writable by the
// user (see add_paid_books_schema.sql), and the PDF itself is only reachable
// through `get-book-url`. So everything below is safe to be wrong about — a
// tampered client can make the UI lie about ownership, but it still cannot open
// a PDF it has not paid for.

export interface PaidBook {
  id: number;
  /** Matches the in-app product ID in Play Console. The key for everything. */
  play_product_id: string;
  title_ml: { it: string; bn: string; en: string };
  description_ml: { it: string; bn: string; en: string };
  cover_url?: string | null;
  level: string;
  /** Display-only fallback. Play is the source of truth for what a user pays. */
  price_display?: string | null;
  order_index: number;
}

export interface BookPurchase {
  play_product_id: string;
  acknowledged: boolean;
  purchased_at: string | null;
}

// ─── Catalogue ────────────────────────────────────────────────────────────────

/**
 * Published paid books, in display order.
 *
 * Note the select list: `storage_path` is deliberately NOT requested. The client
 * has no use for it — only `get-book-url` resolves a path to a downloadable URL
 * — and not fetching it keeps the object layout of a private bucket out of the
 * app's memory and out of any error report.
 *
 * Unlike `fetchEbooks()` there is no FALLBACK constant to return on failure.
 * Hardcoded demo rows would put a Buy button in front of a product ID that may
 * not exist in Play Console, which fails at the billing dialog with an opaque
 * error. An empty catalogue simply renders no section, which is honest.
 */
export async function fetchPaidBooks(): Promise<PaidBook[]> {
  try {
    const { data, error } = await supabase
      .from('paid_books')
      .select('id, play_product_id, title_ml, description_ml, cover_url, level, price_display, order_index')
      .eq('is_published', true)
      .order('order_index', { ascending: true });
    if (error) throw error;
    return (data || []) as PaidBook[];
  } catch {
    return [];
  }
}

// ─── Entitlements ─────────────────────────────────────────────────────────────

/**
 * The caller's own unrevoked purchases — the authoritative Owned/Buy signal.
 *
 * `revoked_at` is set when Google reports a refund or chargeback, so filtering
 * on it is what actually takes a book away; a refunded row stays in the table
 * for audit. Filtering must therefore be `IS NULL`, never "row exists".
 *
 * `userId` is filtered explicitly even though RLS already restricts this table
 * to `auth.uid() = user_id`. That is not redundancy for its own sake: it hits
 * idx_book_purchases_user, and it makes the intent readable at the call site.
 *
 * Guests have no Supabase session, so RLS would return zero rows anyway — the
 * early return just saves the round trip and matches ebookApi's 'guest' guard.
 */
export async function fetchMyPurchases(userId?: string): Promise<BookPurchase[]> {
  if (!userId || userId === 'guest') return [];
  try {
    const { data, error } = await supabase
      .from('book_purchases')
      .select('play_product_id, acknowledged, purchased_at')
      .eq('user_id', userId)
      .is('revoked_at', null);
    if (error) throw error;
    return (data || []) as BookPurchase[];
  } catch {
    // Offline or transient: report "owns nothing" rather than throwing. The
    // Buy button is a dead end without a network anyway, and `get-book-url`
    // re-checks the entitlement server-side before handing over any PDF.
    return [];
  }
}

// ─── Signed URL ───────────────────────────────────────────────────────────────

/**
 * Why the server would not hand over a book.
 *
 * The reader needs to tell "you never bought this" (a dead end — send them back
 * to the library) from "your session expired" (send them to /login) from "the
 * network is down" (offer a retry). Those three are only distinguishable by HTTP
 * status, which supabase-js buries on `error.context`, so it is resolved here
 * once rather than at every call site.
 *
 * Note this is the ONLY distinction available. Within a 403 the function answers
 * "not entitled", "no such book" and "refunded" identically on purpose, so that
 * the client cannot be used to probe the catalogue.
 */
export type BookAccessReason =
  /** No session, or one the Auth server no longer accepts. */
  | 'unauthenticated'
  /** 403: not entitled, refunded, or nonexistent. Indistinguishable by design. */
  | 'not-purchased'
  /** Server error, offline, CORS — anything that is not a verdict on ownership. */
  | 'unavailable';

/** Carries the coarse reason alongside the server's own display message. */
export class BookAccessError extends Error {
  readonly reason: BookAccessReason;

  constructor(reason: BookAccessReason, message: string) {
    super(message);
    this.name = 'BookAccessError';
    this.reason = reason;
  }
}

/**
 * Exchanges a product ID for a short-lived signed URL to the private PDF.
 *
 * NEVER cache, persist, or log the returned string. It is a bearer credential:
 * anyone holding it downloads the book with no login, no app, and no purchase.
 * A URL written to localStorage, to a state store that survives navigation, or
 * to a crash report is a shareable download link for a paid book, and the 300
 * second TTL is the only thing limiting the damage. Call this again — it is
 * cheap — rather than holding one.
 *
 * Throws `BookAccessError` (rather than returning null) so the reader can branch
 * on `.reason` and still show the server's own message. The 403 text is
 * deliberately identical for "you don't own this", "no such book", and "that was
 * refunded", so there is nothing to leak by displaying it.
 */
export async function getSignedBookUrl(productId: string): Promise<string> {
  // Identity travels only in the bearer JWT. With no session, supabase-js sends
  // the anon key instead, which the function explicitly rejects — check first so
  // the user gets "sign in" rather than a bare 401.
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData?.session) {
    throw new BookAccessError('unauthenticated', 'SIGN_IN_REQUIRED');
  }

  const { data, error } = await supabase.functions.invoke('get-book-url', {
    body: { productId },
  });

  // supabase-js turns any non-2xx into a FunctionsHttpError and leaves `data`
  // null, so both the status and the function's own `error` string are only
  // reachable by re-reading the Response off error.context.
  if (error) {
    const ctx = (error as { context?: Response }).context;
    const status = ctx?.status;

    let serverMessage = '';
    try {
      if (ctx && typeof ctx.json === 'function') {
        const body = await ctx.json();
        serverMessage = body?.error || '';
      }
    } catch {
      // Body already consumed or not JSON — fall through to the generic message.
    }

    const reason: BookAccessReason =
      status === 403 ? 'not-purchased' : status === 401 ? 'unauthenticated' : 'unavailable';

    throw new BookAccessError(
      reason,
      serverMessage || error.message || 'Could not open this book.',
    );
  }

  // Contract: { success: true, data: { signedUrl, productId, expiresIn, expiresAt } }
  const signedUrl = data?.data?.signedUrl;
  if (!data?.success || typeof signedUrl !== 'string' || !signedUrl) {
    throw new BookAccessError('unavailable', data?.error || 'Could not open this book.');
  }

  return signedUrl;
}
